/**
 * The adapters talk to apps/api only through this interface. The game's own
 * ApiClient (auth, refresh, retries) satisfies it structurally; FetchApiClient is
 * a small default for tools, tests and help mode.
 */
export interface ApiClient {
  call<T>(method: string, path: string, body?: unknown): Promise<T>;
}

/** Route table in one place; the contract is packages/schemas (api.ts, api-r1.ts). */
export const ROUTES = {
  store: '/v1/store',
  purchaseDev: '/v1/purchases/dev',
  purchaseVerify: '/v1/purchases/verify',
  stripeCheckout: '/v1/purchases/stripe/checkout',
  emailStart: '/v1/auth/email/start',
  emailVerify: '/v1/auth/email/verify',
  /** POST links an id token; DELETE removes that sign-in method */
  linkIdentity: (provider: string) => `/v1/auth/link/${encodeURIComponent(provider)}`,
  linkResolve: '/v1/auth/link/resolve',
  /** a single-use nonce for the next id token (web and native sign-ins) */
  authNonce: '/v1/auth/nonce',
  clientErrors: '/v1/client-errors',
} as const;

export class ApiHttpError extends Error {
  override name = 'ApiHttpError';
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`HTTP ${status}${body && typeof body === 'object' && 'error' in body ? `: ${String(body.error)}` : ''}`);
  }
}

/**
 * For routes where HTTP 409 is an expected answer rather than a failure (account-link
 * conflicts, MO-06 purchase blocks): returns the 409 body when `accept` recognises it,
 * and rethrows everything else. Use as `.catch(conflictBody(isX))`.
 */
export function conflictBody<C>(accept: (body: unknown) => body is C): (e: unknown) => C {
  return (e) => {
    if (e instanceof ApiHttpError && e.status === 409 && accept(e.body)) return e.body;
    throw e;
  };
}

export interface FetchApiOptions {
  baseUrl: string;
  /** bearer access token, if any */
  token?: () => string | null | Promise<string | null>;
  fetch?: typeof fetch;
  timeoutMs?: number;
}

export class FetchApiClient implements ApiClient {
  private readonly fetch: typeof fetch;
  private readonly base: string;

  constructor(private readonly o: FetchApiOptions) {
    this.fetch = o.fetch ?? globalThis.fetch.bind(globalThis);
    this.base = o.baseUrl.replace(/\/$/, '');
  }

  async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await this.o.token?.();
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers.authorization = `Bearer ${token}`;
    const res = await this.fetch(this.base + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(this.o.timeoutMs ?? 10_000),
    });
    const text = await res.text();
    const json = (res.headers.get('content-type') ?? '').includes('json');
    const data: unknown = text && json ? JSON.parse(text) : text || null;
    if (!res.ok) throw new ApiHttpError(res.status, data);
    return data as T;
  }
}
